import { showErrorNotification } from '@vrplatform/theme';
import type { Maybe } from '@vrplatform/utils';
import { useAutomationHistoryDrawer } from '../../../../hooks';

type Automation = {
  automationId: string;
  status: Maybe<string>;
};

export function useStatementGenerateMutation(
  statementAutomations: Automation[],
  statementBillAutomations: Automation[]
) {
  const { runTask, runTaskLoading } = useAutomationHistoryDrawer();

  const generate = async (
    {
      date,
      ids = [],
    }: {
      date: string;
      ids: string[];
    },
    automations: Automation[],
    type: 'statement' | 'bill'
  ) => {
    const res = checkAutomations(automations);
    if (!res.ok) return;

    const i = ids.length > 0 ? ids : undefined;

    try {
      const params: Record<string, any> = {};

      if (type === 'statement') {
        params.month = date;
      }
      params[type === 'bill' ? 'ownerStatementId' : 'listingId'] = i;

      const task = await runTask(
        {
          automationId: res.automationId,
          params,
        },
        {
          refreshQueryKeys: ['ownerStatements'],
        }
      );

      return task;
    } catch (error) {
      console.error(error);
    } finally {
    }
  };

  return {
    generate: (opts: Parameters<typeof generate>[0]) =>
      generate(opts, statementAutomations, 'statement'),
    repost: (opts: Parameters<typeof generate>[0]) =>
      generate(opts, statementBillAutomations, 'bill'),
    loading: runTaskLoading,
  };
}

const checkAutomations = (
  automations: Automation[]
): { ok: true; automationId: string } | { ok: false } => {
  if (!automations?.length) {
    showErrorNotification({
      title: 'Missing owner statement automation',
      message:
        'Please create an owner statement automation to generate statements.',
    });

    return {
      ok: false,
    };
  }

  let automationId: string | null = null;

  const actives = automations.filter(({ status }) => status === 'active');

  if (actives.length > 1) {
    showErrorNotification({
      title: 'Multiple active owner statement automations',
      message:
        'Please deactivate all but one owner statement automation to generate statements.',
    });

    return {
      ok: false,
    };
  }

  automationId = actives?.[0]?.automationId;

  if (!automationId) {
    showErrorNotification({
      title: 'No active owner statement automation',
      message:
        automations.length === 1
          ? 'Please activate your owner statement automation to generate statements.'
          : 'Please activate one of your owner statement automations to generate statements.',
    });

    return {
      ok: false,
    };
  }

  return {
    ok: true,
    automationId,
  };
};
